let currentVariantId;
let productData;
let arrayOfSwymCollectionsButtons;
let showSwymWishlistButton;
let showSwymWishlistCloseButton;
let selectedVariant = {};
let globalListArray = [];
let productURL;
let swat;
let settings;
let sectionId;
let elements;
let wishlistPopup;
let listId;
let swymLists = [];
let isWishlistCallbackExecuting = false;
let wishlistCallbackQueue = [];

// All user-visible copy comes from layout/theme.liquid: theme.strings is locale-resolved and
// theme.wishlistStrings is market-gated. villoid.se serves lang="sv", so this file must carry no
// Norwegian literals of its own. The fallbacks only fire if `theme` is missing entirely.
const wishlistText = (key) => window.theme?.wishlistStrings?.[key] || '';
const stockLeftText = (count) => {
    const template = count === 1
        ? window.theme?.strings?.oneStockLabel
        : window.theme?.strings?.otherStockLabel;
    return (template || 'Kun [count] igjen').replace('[count]', count);
};
const onlyXLeftText = (count) =>
    (window.theme?.strings?.onlyXLeft || 'Bare [count] igjen!').replace('[count]', count);

function getVilloidCartSync() {
    if (window.__villoidCartSync) return window.__villoidCartSync;

    const fetchCart = () => fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then((response) => response.json());

    const visiblePropsSignature = (item) => JSON.stringify(
        Object.entries(item?.properties || {})
            .filter(([key, value]) => key && key.charAt(0) !== '_' && value !== '' && value != null)
            .sort(([a], [b]) => a.localeCompare(b))
    );

    const privatePropsCount = (item) => Object.entries(item?.properties || {})
        .filter(([key, value]) => key && key.charAt(0) === '_' && value !== '' && value != null)
        .length;

    const keyFor = (item) => {
        const sellingPlanId = item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan?.id || '';
        return `${item.variant_id}::${sellingPlanId}::${visiblePropsSignature(item)}`;
    };

    const reconcileVariant = (variantId) => {
        if (!variantId) return Promise.resolve({ changed: false });

        return fetchCart().then((cart) => {
            const groups = {};
            (cart?.items || []).forEach((item, index) => {
                if (String(item.variant_id) !== String(variantId)) return;
                const key = keyFor(item);
                if (!groups[key]) groups[key] = [];
                groups[key].push({ item, index });
            });

            const duplicateGroups = Object.values(groups).filter((group) => group.length > 1);
            if (!duplicateGroups.length) return { changed: false, cart };

            // Bulk /cart/update.js with line-item-key keys collides when two
            // keys share a variant_id prefix (Shopify's parser drops the hash
            // suffix and last-write-wins on quantity, so { keyA: N, keyB: 0 }
            // ends up clearing the variant entirely). Use sequential
            // /cart/change.js calls instead — one line per call, addressed by
            // full key. Survivor set first so the variant is never transiently
            // at qty 0 during the merge.
            const ops = [];
            duplicateGroups.forEach((group) => {
                const survivor = group
                    .slice()
                    .sort((a, b) => {
                        return (
                            (privatePropsCount(b.item) - privatePropsCount(a.item))
                            || ((b.item.quantity || 0) - (a.item.quantity || 0))
                            || (a.index - b.index)
                        );
                    })[0];
                const totalQty = group.reduce((sum, current) => sum + (current.item.quantity || 0), 0);
                ops.push({ id: survivor.item.key, quantity: totalQty });
                group.forEach(({ item }) => {
                    if (item.key !== survivor.item.key) {
                        ops.push({ id: item.key, quantity: 0 });
                    }
                });
            });

            const changeOne = (body) => fetch('/cart/change.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(body)
            }).then((response) => response.json());

            return ops.reduce(
                (chain, body) => chain.then(() => changeOne(body)),
                Promise.resolve()
            ).then((finalCart) => ({ changed: true, cart: finalCart }));
        });
    };

    const state = { queue: Promise.resolve() };
    window.__villoidCartSync = {
        enqueue(task) {
            const run = () => Promise.resolve().then(task);
            state.queue = state.queue.then(run, run).catch(() => undefined);
            return state.queue;
        },
        reconcileVariant,
        refresh() {
            document.dispatchEvent(new CustomEvent('cart:build'));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
    };

    return window.__villoidCartSync;
}

// Initialize once at script load so other bundles (e.g. Nosto) can reuse it.
getVilloidCartSync();

const sizeOrder = [
    'XXS', 'XXS/XS', 'XXS/S', 'XS', 'XS/S', 'XS/32', 'S', 'S/30', 'S/32', 'S/M',
    'M', 'M/32', 'M/L', 'L', 'L/32', 'L/XL', 'XL', 'XL/XXL', 'XXL', '0', '1', '2',
    '4', '5', '6', '7', '7,5', '8', '9', '10', '12', '31', '32', '32/34', '34',
    '34L', '34/L32', '35', '35.5', '35/36', '35/38', '36', '36 ⅓', '36,5', '36.5',
    '36/37', '36 ⅔', '36L', '36/38', '36/L32', '37', '37 ⅓', '37,5', '37.5',
    '37 ⅔', '37/38', '37/39', '38', '38 ⅓', '38,5', '38.5', '38 ⅔', '38L',
    '38/L32', '39', '39 ⅓', '39,5', '39.5', '39 ⅔', '39/40', '39/41', '39/42',
    '40', '40,5', '40.5', '40/41', '40 ⅔', '40L', '40/L32', '41', '41 ⅓',
    '41,5', '41.5', '41/42', '41/43', '41/44', '42', '42,5', '42.5', '42 ⅓',
    '42/43', '42/44', '42/45', '42L', '42/L32', '43', '43 ⅓', '43,5', '43.5',
    '43/44', '43/45', '43/46', '44', '44.5', '44 ⅔', '44L', '44/L32', '44/W32',
    '44/45', '44/46', '44/47', '45', '45.5', '45/46', '45/47', '45/48', '46',
    '46,5', '46/47', '46/48', '47', '47,5', '47/48', '48', '49', '50', '51',
    '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '70', '75',
    '80', '85', '90', '95', '105', 'Høyre', 'One Size', 'Venstre', 'W24',
    'W24/L30', 'W24/L32', 'W24/L34', 'W24/L36', 'W25', 'W25/L30', 'W25/L32',
    'W25/L34', 'W25/L36', 'W26', 'W26/L30', 'W26/L32', 'W26/L34', 'W26/L36',
    'W27', 'W27/L30', 'W27/L32', 'W27/L34', 'W27/L36', 'W28', 'W28/L30',
    'W28/L32', 'W28/L34', 'W28/L36', 'W29', 'W29/L30', 'W29/L32', 'W29/L34',
    'W29/L36', 'W30', 'W30/L30', 'W30/L32', 'W30/L34', 'W30/L36', 'W31',
    'W31/L30', 'W31/L32', 'W31/L34', 'W31/L36', 'W32', 'W32/L30', 'W32/L32',
    'W32/L34', 'W32/L36', 'W33', 'W33/L30', 'W33/L32', 'W33/L34', 'W33/L36',
    'W34', 'W34/L30', 'W34/L32', 'W34/L34',
];

const openWishlist = () => {
    document.querySelector('[data-event=header-wishlist_open]').click();
}

const closeWishlist = () => {
    const isVisible = document.querySelector('.swym-modal').style.display !== "none";
    if (isVisible) {
        document.querySelector('button.swym-close-btn .swym-icon').click();
    }
}

const openCartDrawer = () => {
    document.querySelector('#header-cart-link').click();
}

const shareList = () => {
    // Check if the list has items
    const currentList = swymLists.sort((a, b) => b?.listcontents?.length - a?.listcontents?.length)?.[0];
    if (!currentList || !currentList.listcontents || currentList.listcontents.length === 0) {
        customErrorHandling(wishlistText('emptyList') || 'Ønskelisten er tom. Vennligst legg til produkter før du deler.');
        return;
    }
    // console.log(JSON.stringify({currentList, swymLists}, null, '\t'));
    let lid = currentList.lid;

    // Show loading state
    const shareButton = document.querySelector('#swym-share-wishlist-btn');
    if (shareButton) {
        const originalText = shareButton.innerHTML;
        shareButton.disabled = true;

        // First mark the list as public
        swat.markListPublic(lid, (success) => {
                console.log("List marked as public successfully");
                // Then generate the shared list URL
                swat.generateSharedListURL(lid, (sharedUrlResponse) => {
                console.log("Shared URL generated:", sharedUrlResponse);
                showShareModal(sharedUrlResponse);
                // Show success message
                customSuccessHandling(wishlistText('shared') || 'Ønskelisten din er nå delt!');

                // native navigator share
                navigator.share({
                    title: wishlistText('shareSheetTitle') || 'Deling av ønskeliste',
                    text: wishlistText('shareSheetText') || 'Ønskeliste',
                    url: sharedUrlResponse,
                });

                // copy to clipboard
                navigator.clipboard.writeText(sharedUrlResponse);

                // ga4 event of sharing wishlist
                wishlist_share();

                // Reset button state
                shareButton.innerHTML = originalText;
                shareButton.disabled = false;
            }, (urlError) => {
                console.error("Error generating shared URL:", urlError);
                customErrorHandling(wishlistText('linkError') || 'Kunne ikke generere delingslenke');
                // Reset button state
                shareButton.innerHTML = originalText;
                shareButton.disabled = false;
            });
        }, (markError) => {
            console.error("Error marking list public:", markError);
            customErrorHandling(wishlistText('publicError') || 'Kunne ikke gjøre listen offentlig');
            // Reset button state
            shareButton.innerHTML = originalText;
            shareButton.disabled = false;
        });
    }
}

// Function to check if list is already public
const checkListPublicStatus = (lid, callback) => {
    // This is a simplified check - you might need to implement this based on your Swym setup
    // For now, we'll assume the list needs to be marked as public
    callback(false);
};

// Function to show share modal with the shared URL
const showShareModal = (sharedUrl) => {
    // Remove existing share modal if any
    const existingModal = document.querySelector('.swym-share-modal');
    if (existingModal) {
        existingModal.remove();
    }

    // Create share modal
    const shareModal = document.createElement('div');
    shareModal.className = 'swym-share-modal swym-modal swym-simple-wishlist-modal';
    shareModal.style.display = 'flex';
    shareModal.style.zIndex = '10001'; // Higher than wishlist modal

    shareModal.innerHTML = `
        <div class="swym-modal-bg" aria-hidden="true"></div>
        <div class="swym-modal-content" role="dialog" tabindex="0" aria-labelledby="swym-share-modal-title">
            <button class="swym-close-btn swym-bg-1 swym-is-button" aria-label="${window.theme?.strings?.closeModal || 'Lukk'}">
                <span class="swym-icon"></span>
            </button>
            <div class="swym-simple-wishlist-container">
                <div class="swym-simple-wishlist-container-content">
                    <div class="swym-wishlist-detail swym-simple-wishlist-detail">
                        <div class="swym-wishlist-detail-header">
                            <h2 class="swym-wishlist-main-title swym-heading swym-heading-1 swym-is-heading"
                                id="swym-share-modal-title">${wishlistText('shareTitle') || 'Del ønskeliste'}</h2>
                        </div>
                        <div class="swym-wishlist-detail-content">
                            <div style="text-align: center; margin-bottom: 30px;">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="#28a745" style="margin-bottom: 15px;">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                                <p style="margin-bottom: 20px; color: #666; font-size: 16px;">${wishlistText('shareBody') || 'Din ønskeliste er nå offentlig og kan deles med andre.'}</p>
                            </div>
                            <div class="swym-share-url-container" style="margin: 20px 0;">
                                <label for="swym-share-url" style="display: block; margin-bottom: 8px; font-weight: bold; color: #333;">${wishlistText('shareLinkLabel') || 'Delingslenke:'}</label>
                                <div style="display: flex; gap: 10px; align-items: center;">
                                    <input type="text" id="swym-share-url" value="${sharedUrl}" readonly 
                                           style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 6px; font-family: monospace; font-size: 14px; background-color: #f8f9fa; cursor: text;">
                                    <button id="swym-copy-url" class="swym-button swym-bg-1" 
                                            style="padding: 12px 16px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; transition: background-color 0.2s; white-space: nowrap;">
                                        ${wishlistText('copy') || 'Kopier'}
                                    </button>
                                </div>
                                <p style="margin-top: 8px; font-size: 12px; color: #666; text-align: center;">
                                    ${wishlistText('shareHelp') || 'Klikk på lenken for å teste den, eller bruk kopier-knappen for å dele den'}
                                </p>
                            </div>
                            <div class="swym-share-actions" style="margin-top: 30px; text-align: center;">
                                <button id="swym-close-share" class="swym-button swym-bg-2" 
                                        style="padding: 12px 24px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; transition: background-color 0.2s;">
                                    ${window.theme?.strings?.close || 'Lukk'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Add modal to body
    document.body.appendChild(shareModal);

    // Add event listeners
    const closeBtn = shareModal.querySelector('.swym-close-btn');
    const closeButton = shareModal.querySelector('#swym-close-share');
    const copyButton = shareModal.querySelector('#swym-copy-url');

    const closeShareModal = () => {
        shareModal.remove();
    };

    closeBtn.addEventListener('click', closeShareModal);
    closeButton.addEventListener('click', closeShareModal);

    // Copy URL functionality
    copyButton.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(sharedUrl);
            copyButton.textContent = wishlistText('copied') || 'Kopiert!';
            copyButton.style.backgroundColor = '#28a745';
            setTimeout(() => {
                copyButton.textContent = wishlistText('copy') || 'Kopier';
                copyButton.style.backgroundColor = '';
            }, 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = sharedUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            copyButton.textContent = wishlistText('copied') || 'Kopiert!';
            copyButton.style.backgroundColor = '#28a745';
            setTimeout(() => {
                copyButton.textContent = wishlistText('copy') || 'Kopier';
                copyButton.style.backgroundColor = '';
            }, 2000);
        }
    });

    // Make URL input clickable to select all text
    const urlInput = shareModal.querySelector('#swym-share-url');
    urlInput.addEventListener('click', () => {
        urlInput.select();
        urlInput.setSelectionRange(0, 99999); // For mobile devices
    });

    // Close modal when clicking outside
    const modalBg = shareModal.querySelector('.swym-modal-bg');
    modalBg.addEventListener('click', closeShareModal);

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeShareModal();
        }
    });
}


function customErrorHandling(error) {
    _swat.ui.uiRef.showErrorNotification({message: error});
}

function customSuccessHandling(success, onClick) {
    _swat.ui.uiRef.showSuccessNotification({message: success});
    const mainSwymContainer = _swat.ui.uiRef.mainContainer;
    const swymNotificationContainer = mainSwymContainer.querySelector('.swym-wishlist-notifications-container');
    swymNotificationContainer.addEventListener('click', () => {
        onClick ? onClick() : openWishlist();
    })
}

async function persistWishlistButtonsState() {
    return new Promise((resolve, reject) => {
        let onSuccess = function (lists) {
            globalListArray = lists;

            if (lists.length < 1) {
                let listConfig = {
                    "lname": "My Wishlist",
                };

                let onSuccess = function (newList) {
                    // List created
                };

                let onError = function (error) {
                    console.error("Error while creating a List", error);
                };

                swat.createList(listConfig, onSuccess, onError);
            }

            const productIdsInList = new Set();

            // First pass: Add 'swym-added' class to buttons in the list and collect the product IDs.
            lists.forEach((list) => {
                list.listcontents.forEach((product) => {
                    let productId = product.empi;
                    productIdsInList.add(String(productId));
                    
                    // Handle both product page buttons (with id="swym-collections") and cart buttons
                    let buttonsToFill = document.querySelectorAll(`button.swym-button[data-product-id="${productId}"]`);
                    buttonsToFill.forEach((buttonToFill) => {
                      if (buttonToFill && !buttonToFill.classList.contains('swym-added')) {
                        SwymUtils.addClass(buttonToFill, "swym-added");
                      }
                    })
                });
            });
            
            // Second pass: Remove 'swym-added' class from buttons not in the list.
            // Handle both product page buttons and cart buttons
            const toRemoveButtons = document.querySelectorAll('button.swym-button.swym-added');
            toRemoveButtons.forEach((button) => {
                const productId = button.getAttribute('data-product-id');
                if (!productIdsInList.has(productId) && button.classList.contains('swym-added')) {
                    SwymUtils.removeClass(button, 'swym-added');
                }
            });
            resolve();
        };

        let onError = function (error) {
            console.error("Error while fetching all Lists", error);
            reject(error);
        };
        swat.fetchLists({
            callbackFn: onSuccess,
            errorFn: onError,
        });
    });
}

function onSwymLoadCallback(swatInstance) {
    swat = swatInstance || window._swat;

    function debounce_leading(func, timeout = 300) {
        let timer;
        return (...args) => {
            if (!timer) {
                func.apply(this, args);
            }
            clearTimeout(timer);
            timer = setTimeout(() => {
                timer = undefined;
            }, timeout);
        };
    }

    function handleAddedToWishlistButtonState(productId, variantId) {
        const buttonToFill = document.querySelector(`button.swym-button[data-product-id="${productId}"]`);
        if (buttonToFill) {
            SwymUtils.addClass(buttonToFill, "swym-added");
            // Update the data-variant-id to the variant that was added
            buttonToFill.setAttribute('data-variant-id', variantId);
        }
    }

    function removeProductFromWishlist(productId, variantId, productURL, button) {
        const product = {
            epi: variantId,
            empi: productId,
            du: productURL
        };
        console.log(`Product to remove ${product.empi}, ${product.epi}`)

        const onSuccess = function () {
            console.log(`[removeProductFromWishlist.onSuccess] running`)
            SwymUtils.removeClass(button, 'swym-added');
            button.removeAttribute('data-variant-id');
            customSuccessHandling(wishlistText('removed') || 'Produktet har blitt fjernet fra ønskelisten din');
            setTimeout(() => fetchWishlist(), 1000);
        };

        const onError = function (error) {
            console.error("Error while deleting the Product", error);
            customErrorHandling(error.description);
            setTimeout(() => fetchWishlist(), 1000);
        };

        globalListArray.forEach((list) => {
            list.listcontents.forEach((p) => {
              console.log(`p.empi ${p.empi}, p.epi ${p.epi}`)
              if (p.empi == productId && p.epi == variantId) {
                console.log(`[removeProductFromWishlist] found productId: ${productId}, variantId: ${variantId} on list ${list.lid}`)
                let lid = list?.lid;
                if (lid) {
                    _swat.deleteFromList(lid, product, onSuccess, onError);
                    console.log(`Removed from wishlist next is fetchWishlist`);
                    fetchWishlist();
                } else {
                    customErrorHandling(wishlistText('noList') || 'Ingen ønskeliste finnes');
                }
              }
            })
        })
    }


    function addVariantToWishlist(variantId, productId, productURL) {
        var product = {
            epi: variantId,
            empi: productId,
            du: productURL
        };

        let onSuccess = function (addedListItem) {
            let updateMessage = (wishlistText('added') || '[title] har blitt lagt til i ønskelisten din').replace('[title]', productData.product.title);
            customSuccessHandling(updateMessage);
            // Update the wishlist button state
            handleAddedToWishlistButtonState(productId, variantId);
            setTimeout(() => fetchWishlist(), 1000);
        };

        let onError = function (error) {
            console.error("Error while adding the Product to the Wishlist", error);
            customErrorHandling(error.description);
            setTimeout(() => fetchWishlist(), 1000);
        };

        // Assuming that for single wishlist, we have the list ID
        let lid;
        if (globalListArray && globalListArray.length > 0) {
            lid = globalListArray[0].lid;
            _swat.addToList(lid, product, onSuccess, onError);
        } else {
            // Handle case where no lists exist by creating a default list
            let listConfig = {
                "lname": "My Wishlist",
            };

            _swat.createList(listConfig, function (newList) {
                lid = newList.lid;
                _swat.addToList(lid, product, onSuccess, onError);
            }, function (error) {
                console.error("Error while creating a List", error);
                customErrorHandling(error.description);
                fetchWishlist();
            });
        }
    }

    function openWishlistPopup(event, productData) {
        // Remove existing wishlist popups to avoid duplicates
        const existingPopup = document.querySelector('.ns-hover-menu-wishlist');
        if (existingPopup) {
            existingPopup.parentNode.removeChild(existingPopup);
        }

        // Create popup element
        wishlistPopup = document.createElement('div');
        wishlistPopup.className = 'ns-hover-menu-wishlist ns-active';

        // Position the popup near the button
        const button = event.target || document.querySelector('.page-content--product .swym-add-to-wishlist-view-product');
        const gridProductMeta = button.closest('.grid-product__meta');
        if (!gridProductMeta) return;
        const rect = button.getBoundingClientRect();
        // wishlistPopup.style.position = 'absolute';
        // wishlistPopup.style.top = '100%';
        // wishlistPopup.style.left = '0';
        // wishlistPopup.style.zIndex = 1000;

        // Get the variants
        const variants = productData.product.variants;

        if (variants.length === 1) {
            // If there is only one variant, we just add the product to the wishlist
            addVariantToWishlist(variants[0].id, productData.product.id, productURL);
            setTimeout(() => {
                fetchWishlist();
            }, 500);
            return;
        }

        // if button has a data-variant-id, use that variant ID
        const buttonVariantId = button.getAttribute('data-variant-id');
        const isWithinCart = button.closest('.cart__item');
        console.log({
          button,
          buttonVariantId,
          isWithinCart,
        });
        if (buttonVariantId && isWithinCart) {
            addVariantToWishlist(buttonVariantId, productData.product.id, productURL);
            return;
        }

        // Find the index of the 'Size' option
        const sizeOptionIndex = productData.product.options.findIndex(
            option => option.name && option.name.toLowerCase() === 'size'
        );

        let sortedVariants;

        if (sizeOptionIndex !== -1) {
            // We have a 'Size' option
            sortedVariants = variants.slice(); // Create a copy to avoid mutating the original

            // Filter out variants that have the size option
            sortedVariants = sortedVariants.filter(variant => {
                const sizeOption = variant['option' + (sizeOptionIndex + 1)];
                return sizeOption !== undefined && sizeOption !== null;
            });

            sortedVariants.sort((a, b) => {
                const sizeA = a['option' + (sizeOptionIndex + 1)];
                const sizeB = b['option' + (sizeOptionIndex + 1)];

                // Handle cases where sizeA or sizeB is undefined
                if (!sizeA && !sizeB) return 0;
                if (!sizeA) return 1;
                if (!sizeB) return -1;

                const indexA = sizeOrder.indexOf(sizeA);
                const indexB = sizeOrder.indexOf(sizeB);

                if (indexA === -1 && indexB === -1) {
                    // Both sizes not found in sizeOrder, compare alphabetically
                    return sizeA.localeCompare(sizeB);
                } else if (indexA === -1) {
                    // SizeA not found, put it after SizeB
                    return 1;
                } else if (indexB === -1) {
                    // SizeB not found, put it after SizeA
                    return -1;
                } else {
                    // Both sizes found, compare their indices
                    return indexA - indexB;
                }
            });
        } else {
            // 'Size' option not found, use default variant order
            sortedVariants = variants;
        }

        // Create the container for the dropdown
        const dropdownContainer = document.createElement('li');
        dropdownContainer.className = 'ns-sidebar-dropdown ns-dropdown ns-not-facet-dropdown ns-list-unstyled';

        // Create the dropdown title
        const dropdownTitle = document.createElement('a');
        dropdownTitle.className = [
            'ns-quickbuy-title',
            'ns-sidebar-facet-title',
            'ns-product-dropdown',
            'ns-d-inline-block',
            'ns-relative',
            'ns-w-100',
            'ns-border-box',
            'ns-text-undecorated'
        ].join(' ');
        dropdownTitle.href = 'javascript:void(0)';
        dropdownTitle.textContent = window.theme?.strings?.addToWishlist || 'Legg til ønskeliste';

        // Create the submenu container
        const submenuContainer = document.createElement('div');
        submenuContainer.className = 'ns-sidebar-submenu';

        // Create the list for variant options
        const variantOptionsList = document.createElement('ul');
        variantOptionsList.className = 'ns-dropdown-wrapper ns-list-unstyled ns-p-0 ns-m-0';

        // For each variant, create a list item
        sortedVariants.forEach((variant, index) => {
            // if (variant.inventory_quantity == 0) {
            //     return
            // }
            const listItem = document.createElement('li');
            listItem.className = 'ns-dropdown-option ns-d-flex ns-align-items-center';
            listItem.setAttribute('data-variant-id', variant.id);
            listItem.setAttribute('data-product-id', productData.product.id);

            // Create the variant title div
            const variantValueDiv = document.createElement('div');
            variantValueDiv.className = 'ns-option-variant-value option-variant-value';

            // Construct the variant title using option values
            let variantOptions = [];
            for (let i = 1; i <= 3; i++) {
                const optionValue = variant['option' + i];
                if (optionValue && optionValue !== 'Default Title') {
                    variantOptions.push(optionValue);
                }
            }
            let variantTitle = variantOptions[0];
            variantValueDiv.textContent = variantTitle;
            listItem.appendChild(variantValueDiv);

            // Optionally, add availability or stock information
            if (variant.inventory_quantity < 4) {
                const outOfStockDiv = document.createElement('div');
                if (productData?.tags?.includes("HYPE") || productData?.product?.tags?.includes("HYPE")) {
                    outOfStockDiv.className = `ns-option-context text-black presale-false`;
                    outOfStockDiv.textContent = ``;
                } else if ((productData?.tags?.includes("SHOW_SOLD_OUT") || productData?.product?.tags?.includes("SHOW_SOLD_OUT")) && variant.inventory_quantity < 2) {
                    outOfStockDiv.className = `ns-option-context text-black presale-false`;
                    outOfStockDiv.textContent = window.theme?.strings?.soldOut || 'Utsolgt';
                } else if (variant.inventory_quantity < 1) {
                    outOfStockDiv.className = `ns-option-context text-black presale-false`;
                    outOfStockDiv.textContent = window.theme?.strings?.soldOut || 'Utsolgt';
                } else {
                    outOfStockDiv.className = `ns-option-context option-${index}-left presale-false`;
                    outOfStockDiv.textContent = stockLeftText(productData?.product?.tags?.includes("SHOW_SOLD_OUT") ? variant.inventory_quantity-1 : variant.inventory_quantity);
                }
                listItem.appendChild(outOfStockDiv);
            }

            // Add click event listener to add to wishlist
            listItem.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                addVariantToWishlist(variant.id, productData.product.id, productURL);
                // Close the popup
                if (wishlistPopup.parentNode) {
                    wishlistPopup.parentNode.removeChild(wishlistPopup);
                }
            });

            variantOptionsList.appendChild(listItem);
        });

        submenuContainer.appendChild(variantOptionsList);
        dropdownContainer.appendChild(dropdownTitle);
        dropdownContainer.appendChild(submenuContainer);
        wishlistPopup.appendChild(dropdownContainer);

        // Add the popup to the body
        gridProductMeta.appendChild(wishlistPopup);

        // Close the popup when clicking outside
        function closeWishlistPopup(e) {
            if (!wishlistPopup.contains(e.target) && e.target !== button) {
                if (wishlistPopup.parentNode) {
                    wishlistPopup.parentNode.removeChild(wishlistPopup);
                }
                document.removeEventListener('click', closeWishlistPopup);
            }
        }

        setTimeout(() => {
            document.addEventListener('click', closeWishlistPopup);
        }, 0);
    }


    function fetchProductDetails(event) {
        event.preventDefault();
        event.stopPropagation();
        const rawUrl = event.target.getAttribute("data-product-url");
        productURL = rawUrl.split("?")[0];
        const shopifyProductEndpoint = productURL + ".json";
        fetch(shopifyProductEndpoint)
            .then((res) => res.json())
            .then((productJson) => {
                productData = productJson;
                selectedVariant = productData.product.variants[0];
                openWishlistPopup(event, productData);
            });
    }

    function addEventListeners() {
        showSwymWishlistButton = document.querySelectorAll(".swym-wishlist");
        showSwymWishlistCloseButton = document.querySelectorAll(".swym-close-btn .swym-icon");
        const modalContent = document.querySelector('.swym-simple-wishlist-modal');
        arrayOfSwymCollectionsButtons = document.querySelectorAll(".swym-add-to-wishlist-view-product");

        // Add share button event listener
        const shareButton = document.querySelector('#swym-share-wishlist-btn');
        shareButton.style = "";

        if (arrayOfSwymCollectionsButtons) {
            for (let i = 0; i < arrayOfSwymCollectionsButtons.length; i++) {
                arrayOfSwymCollectionsButtons[i].addEventListener("click", function (event) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const button = event.currentTarget;
                    const productId = button.getAttribute("data-product-id");
                    const productURL = button.getAttribute("data-product-url");
                    
                    if (button.classList.contains('swym-added')) {
                        // Product is already in the wishlist, remove it
                        let variantId = button.getAttribute('data-variant-id');
                        // If the product URL contains a variant, use that variant ID is null
                        if (productURL.includes('?variant') && !variantId) {
                          variantId = productURL.split('?variant=')[1];
                        }
                        console.log({
                          productId,
                          variantId,
                          productURL,
                          button,
                        })
                        removeProductFromWishlist(productId, variantId, productURL, button);
                    } else {
                        // Product is not in the wishlist, open variant selector popup
                        debounce_leading(fetchProductDetails, 200)(event);
                    }
                    debounce_leading(fetchWishlist, 500)(event);
                });
                SwymUtils.addClass(arrayOfSwymCollectionsButtons[i], "swym-custom-loaded");
            }
        }

        if (showSwymWishlistButton.length) {
            showSwymWishlistButton.forEach(function (button) {
                button.addEventListener("click", function (event) {
                    modalContent.style.display = 'flex';
                    document.querySelector('body').style.overflow = 'hidden';
                    
                    // Initialize share button after modal is shown
                    setTimeout(() => {
                        const shareButton = document.querySelector('#swym-share-wishlist-btn');
                        if (shareButton && !shareButton.hasAttribute('data-event-bound')) {
                            shareButton.setAttribute('data-event-bound', 'true');
                            shareButton.addEventListener('click', (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                shareList();
                            });
                        }
                    }, 100);
                });
            })
        }
        if (showSwymWishlistCloseButton.length) {
            showSwymWishlistCloseButton.forEach(function (button) {
                button.addEventListener("click", function (event) {
                    modalContent.style.display = 'none';
                    document.querySelector('body').style.overflow = 'auto';
                });
            })
        }

        // Initialize wishlist state
        persistWishlistButtonsState();
    }

    addEventListeners();
    setTimeout(() => {
        addEventListeners();
    }, 500);
    window.swymAddEventListeners = addEventListeners;
}

// Track current render operation
let isRendering = null;

async function renderList(list) {
    // Create new render operation
    try {
        if (!list || list.length === 0) {
            return;
        }
        
        if (list.length >= 0) {
            const wishlistedProducts = await fetchProductData(list);
            
            await Promise.all(wishlistedProducts.map(product => {
                return renderProductItem(product);
            }));
        }
    } finally {
    }
}


function initSwymUI(swat) {
    if (swat?.ui) {
        swat.ui.turnOffPopup() // disable default wishlist modal
    }
    const wishlistElement = document.querySelector('.swymcs-custom-wishlist');

    settings = JSON.parse(wishlistElement.dataset.settings);
    sectionId = wishlistElement.dataset.sectionid;
    elements = {
        loader: wishlistElement.querySelector('#swymcs-wishlist-loader'),
        container: wishlistElement.querySelector('#swymcs-wishlist-container'),
        listOptions: wishlistElement.querySelector('#swymcs-wishlist-list-options-container'),
        itemsContainer: wishlistElement.querySelector('#swymcs-wishlist-items-container'),
        emptyContainer: wishlistElement.querySelector('#swymcs-empty-wishlist-container'),
    };
    fetchWishlist();
}

let wasRendered = false

function updateWishlistCount() {
    let totalCount = 0;
    if (swymLists && swymLists.length > 0) {
        swymLists.forEach(list => {
            if (list.listcontents && list.listcontents.length > 0) {
                totalCount += list.listcontents.length;
            }
        });
    }

    const wishlistCountElement = document.getElementById('wishlist-item-count');
    if (wishlistCountElement) {
        wishlistCountElement.textContent = totalCount > 9 ? '9+' : totalCount;
        // Add/remove visible class based on count
        if (totalCount > 0) {
            wishlistCountElement.classList.add('wishlist-link__bubble--visible');
        } else {
            wishlistCountElement.classList.remove('wishlist-link__bubble--visible');
        }
    }
}

function executeWishlistCallback(lists) {
    try {
        lists = lists.reverse();
        swymLists = lists;
        const currentListId = swat.swymCustomWishlistSelectedListId || lists[0]?.lid;
        swat.swymCustomWishlistSelectedListId = currentListId;
        listId = currentListId;
        // Clear existing content
        elements.itemsContainer.innerHTML = '';
        // // Add skeleton loaders while fetching data
        // const skeletonCount = 4; // Number of skeleton items to show
        // for (let i = 0; i < skeletonCount; i++) {
        //     const skeletonItem = `
        //         <div class="ns-product skeleton-loader">
        //             <div class="grid-product__content">
        //                 <div class="skeleton-image"></div>
        //                 <div class="grid-product__meta ns-px-1">

        //                 </div>
        //             </div>
        //         </div>
        //     `;
        //     elements.itemsContainer.innerHTML += skeletonItem;
        // }
        // check if empty array and skip
        if (!lists.length) {
            updateWishlistCount();
            return;
        }
        if (wasRendered) {
          return;
        }
        wasRendered = true;

        for (const list of lists) {
            renderList(list.listcontents);
        }
        // const buyButtons = document.querySelector('.swym-add-to-cart-btn');
        persistWishlistButtonsState();
        updateWishlistCount();
    } finally {
        isWishlistCallbackExecuting = false;
        // Process next item in queue if any
        if (wishlistCallbackQueue.length > 0) {
            const nextCallback = wishlistCallbackQueue.shift();
            isWishlistCallbackExecuting = true;
            nextCallback();
        }
    }
}

function fetchWishlist() {
    wasRendered = false;
    swat.fetchLists({
        callbackFn: (lists) => {
            // Queue execution if callback is already running
            if (isWishlistCallbackExecuting) {
                wishlistCallbackQueue.push(() => executeWishlistCallback(lists));
                return;
            }

            // Execute immediately if no current execution
            isWishlistCallbackExecuting = true;
            executeWishlistCallback(lists);
        },
        errorFn: (error) => console.error('Error while fetching all Lists', error),
    });
}

function getProductUrl(product) {
    // Adjust if you need to include collection handles or other parameters
    return `/products/${product.handle}`;
}

async function fetchSwatchProducts(currentProductData) {
    const swatchProducts = [];
    const currentProductId = currentProductData.id;

    // Extract the STYLENAME from the product tags
    const styleNameTag = currentProductData.tags.find(tag => tag.startsWith('STYLENAME:'));
    let styleName = null;
    if (styleNameTag) {
        styleName = styleNameTag.replace('STYLENAME:', '').trim();
    } else {
        return swatchProducts; // Return empty array if no style name is found
    }

    // Fetch HTML response instead of JSON
    try {
        const response = await fetch(`/search?type=product&q=tag:${encodeURIComponent('STYLENAME:' + styleName)}`);
        const html = await response.text();

        // Parse the HTML to locate the JSON data within the <script> tag
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Locate the script with the product data JSON
        const scriptTag = doc.querySelector('script[id="web-pixels-manager-setup"]');

        if (scriptTag) {
            // Extract the JSON data from the script tag's content
            const scriptContent = scriptTag.textContent;

            // Find JSON data for products, usually within `ShopifyAnalytics.meta`
            const jsonDataMatch = scriptContent.match(/"productVariants":\s*(\[[^\]]+\])/);

            if (jsonDataMatch) {
                const productVariantsData = JSON.parse(jsonDataMatch[1]);

                // Filter out the current product by ID and add remaining to swatchProducts
                productVariantsData.forEach(product => {
                    if (product.product.id !== currentProductId) {
                        swatchProducts.push({
                            id: product.product.id,
                            title: product.product.title,
                            vendor: product.product.vendor,
                            url: product.product.url,
                            image: product.image.src,
                            price: product.price.amount,
                            sku: product.sku,
                            type: product.product.type,
                        });
                    }
                });
            } else {
                console.error("Product data not found in the script content.");
            }
        } else {
            console.error("Script tag containing product data not found.");
        }
    } catch (error) {
        console.error('Error fetching swatch products:', error);
    }

    return swatchProducts;
}

function formatDateToNorwegian(date) {
    return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getBadgeData(productData) {
    const currentDate = new Date();
    const newPreOrderItems = [];

    // Extract variant IDs directly from productData
    const variantIds = productData.variants.map(variant => variant.id);

    productData.tags.forEach((item) => {
        if (item.startsWith('#PREORDER')) {
            const parts = item.split('*');
            if (parts.length > 1) {
                const skuId = parts[0].split('-')[1];
                const preOrderEndDate = new Date(parts[1]);
                const formattedDate = formatDateToNorwegian(preOrderEndDate);

                if (currentDate <= preOrderEndDate && variantIds.includes(skuId)) {
                    newPreOrderItems.push({id: skuId, endDate: formattedDate});
                }
            }
        }
    });

    return {preOrderItems: newPreOrderItems};
}

// Function to render badges
function renderBadges(badgeData, productData) {
    let html = `<div class="badges">`;

    // Render preorder badge if available
    if (badgeData.preOrderItems.length > 0) {
        // Label comes from theme.badges (layout/theme.liquid) so it follows the market.
        const presaleLabel = window.theme?.badges?.presale?.label || window.theme?.strings?.preorder || 'Forhåndsbestill';
        html += `<span class="badge badge-red presale">${presaleLabel}</span>`;
    }

    // Inert mount — DT/sold-out badges rendered by the central engine (window.dtDisplayTags in layout/theme.liquid)
    const tagsAttr = escapeHtml((productData.tags || []).join(', '));
    html += `<span class="badge dt-tag" data-tags="${tagsAttr}" data-available="${productData.available !== false}"></span>`;

    html += `</div>`;
    return html;
}

async function renderProductItem(product, _, wishlistItems) {
    // Access the Shopify product data
    const productData = product.productData;
    const variant = productData.variants.find(v => v.sku === product.sku)
    const variantSize = variant.option1
    const isSoldOut = variant.inventory_quantity < 1
    // Determine if the product is on sale
    const onSale = productData.compare_at_price && productData.compare_at_price > productData.price && productData.tags.includes('#ONSALE:true');

    // Assign images. The trimmer job appends `_trimmed` siblings to the
    // end of each product's image list — prefer those for listing thumbs.
    const allImages = productData.images || [];
    const trimmed = allImages.filter((u) => typeof u === 'string' && u.includes('_trimmed'));
    const sources = allImages.filter((u) => typeof u === 'string' && !u.includes('_trimmed'));
    const previewImage  = sources[0] ? getImageUrl(trimmed[0] || sources[0]) : null;
    const secondaryImage = sources[1] ? getImageUrl(trimmed[1] || sources[1]) : null;
    // Image settings
    const imageRatio = 'portrait';
    const imageClasses = 'grid-image lazyautosizes lazyloaded';
    const numberOfSwatchesToShow = 4;

    // Fetch swatch products (if applicable)
    const swatchProducts = []; // Replace with actual data if available

    // Get badge data
    const badgeData = getBadgeData(productData);
    const badgesHTML = renderBadges(badgeData, productData);

    // Create the wishlist item container
    const wishlistItem = document.createElement('div');
    wishlistItem.classList.add(
        'ns-product',
        `ns-${productData.id}`,
        'ns-border-box',
        'ns-clickable',
        'ns-text-undecorated',
        'ns-color-inherit'
    );
    wishlistItem.setAttribute('data-aos', 'row-of-4');
    wishlistItem.setAttribute('data-product-handle', productData.handle);
    wishlistItem.setAttribute('data-product-id', productData.id);
    wishlistItem.setAttribute('data-variant-id', variant.id);

    // Start building HTML
    let html = `
        <div class="grid-product__content">
            <div class="badges">
                ${badgesHTML}
            </div>
            <div class="ns-overflow-visible grid-product__image-mask">
    `;

    // Begin product link
    const productUrl = getProductUrl(productData);
    html += `
        <a href="${productUrl}" class="grid-product__link">
            <div class="grid-product__image-mask">
                <div class="grid__image-ratio grid__image-ratio--${imageRatio}" style="max-height: 390px;">
    `;

    // Product images
    if (previewImage) {
        html += `<img src="${previewImage}" alt="${escapeHtml(productData.title)}" class="${imageClasses}" loading="lazy" />`;
    } else {
        html += `<div class="product-image--placeholder"></div>`;
    }

    if (secondaryImage) {
        html += `<img src="${secondaryImage}" alt="${escapeHtml(productData.title)}" class="${imageClasses} grid-product__secondary-image" loading="lazy" />`;
    }

    html += `
                </div>
            </div>
        </a>
        </div>
    `;

    // Swatches logic
    if (swatchProducts.length > 1) {
        html += `
            <div class="product-top__colors-block">
                <div class="ns-product-top__colors-list">
        `;

        const swatchesToShow = swatchProducts.slice(0, numberOfSwatchesToShow);
        swatchesToShow.forEach((prod) => {
            const colorOption = prod.options.find(option => option.name.toLowerCase() === 'color');
            const colorSwatch = colorOption ? colorOption.values[0] : '';
            const variantHandleUrl = prod.url.split('/').pop();
            const productType = prod.type || '';
            const swatchImageUrl = getImageUrl(prod.featured_image);
            const isActive = productData.id === prod.id ? 'active' : '';

            html += `
                <a href="${prod.url}"
                    data-url="/products/${variantHandleUrl}"
                    colorSwatch="${escapeHtml(colorSwatch)}"
                    alt="${escapeHtml(prod.title)}"
                    title="${escapeHtml(prod.title)}"
                    data-swatch-image-uri="${swatchImageUrl}"
                    data-swatch-product-type="${productType}"
                    style="background:url(${resizeImageUrl(swatchImageUrl, 300)}) center center / 450% no-repeat;"
                    class="ColorSwatches__Color ${isActive}">
                    ${escapeHtml(prod.title)}
                </a>
            `;
        });

        html += `</div></div>`;
    } else {
        html += `<div class="nosto-no-swatches"></div>`;
    }

    // Product meta
    html += `
        <div class="grid-product__meta ns-px-1">
    `;

    // Quick buy button logic
    const skus = productData.variants;
    if (skus && (skus.length > 1 || (skus.length === 1 && hasValidSize(skus)))) {
        html += `
            <button class="nosto-quickbuy" onclick="handleQuickBuyMenu(event, ${productData.id})">
                <!-- QuickBuyIcon -->
            </button>
        `;
    } else if (skus && skus.length === 1) {
        html += `
            <button class="nosto-quickbuy" onclick="handleAddToCart(event, ${skus[0].id})">
                <!-- AddToCartIcon -->
            </button>
        `;
    }

    // Product title and vendor
    html += `
        <div class="grid-product__title grid-product__title--heading text-uppercase">
            ${escapeHtml(productData.vendor)}
            <button
              id="swymcs-remove-productBtn"
              aria-label="${window.theme?.strings?.remove || 'Fjern'}"
              data-product-id="${productData.id}"
              data-variant-id="${skus[0].id}"
              class="swymcs-wishlistplus-item-remove swym-button swym-add-to-wishlist-view-product swym-icontext swym-heart swym-custom-loaded disabled swym-added swym-loaded"
            ></button>
        </div>
        <div class="grid-product__vendor text-transform-none">
            ${escapeHtml(productData.title)}
        </div>
    `;

    // Price logic
    html += `<div class="grid-product__price ns-grid-product__price">`;

    if (onSale) {
        const discountPercentage = Math.round(
            ((productData.compare_at_price - productData.price) / productData.compare_at_price) * 100
        );
        html += `
            <span class="hide grid-product__price--original"><span class="money">${formatMoney(productData.compare_at_price)}</span></span>
            <span class="money" style="color: #db2e00;">
                ${formatMoney(productData.price)}
            </span>
            <span class="hide grid-product__price-sale ns-grid-product__price-sale" style="color: #db2e00; margin-left: 5px;">
                -${discountPercentage}%
            </span>
        `;
    } else {
        html += `<span class="money">${formatMoney(productData.price)}</span>`;
    }

    html += `</div>`;

    // Hover menu for quick buy
    html += `
        <div class="ns-hover-menu">
            ${generateHoverMenu(productData)}
        </div>
    `;

    const addToCartLabel = window.theme?.strings?.addToCart || 'Legg i handlekurven';
    const notifyLabel = wishlistText('notifyMe') || 'Varsle meg';

    html += `<div class="size-and-amount-line__size">${wishlistText('selectedSize') || 'Valgt størrelse:'} ${escapeHtml(variantSize)}</div>`

    // Stock level logic
    const totalStock = variant.inventory_quantity;
    if (totalStock > 0 && totalStock < 4 && !productData.tags.includes("HYPE") && !productData.tags.includes("SHOW_SOLD_OUT")) {
        html += `<span class="only-x-left ns-only-x-left">${onlyXLeftText(totalStock)}</span>`;
    } else if (totalStock > 1 && productData.tags.includes("SHOW_SOLD_OUT")) {
        html += `<span class="only-x-left ns-only-x-left">${onlyXLeftText(totalStock-1)}</span>`;
    } else {
        html += `<span class="only-x-left ns-only-x-left">&nbsp;</span>`;
    }

    if (productData.tags.includes("HYPE")) { 
    } else {
        if (productData.tags.includes("SHOW_SOLD_OUT") && totalStock > 1) {
            html += `
            <button class="swym-add-to-cart-btn add-to-cart" aria-label="${addToCartLabel}">${addToCartLabel}</button>
            `;
        } else if (isSoldOut || productData.tags.includes("SHOW_SOLD_OUT")) {
            html += `<button class="swym-add-to-cart-btn add-to-cart back-in-stock" aria-label="${notifyLabel}">${notifyLabel}</button>`;
        } else {
            html += `<button class="swym-add-to-cart-btn add-to-cart" aria-label="${addToCartLabel}" data-event="wishlist_add_to_cart">${addToCartLabel}</button>`;
        }
    }

    // Close meta div
    html += `</div>`;

    wishlistItem.innerHTML = html;
    const sharedWishlistContainerElement = document.querySelector('#swym-shared-wishlist-render-container');
    if (sharedWishlistContainerElement) {
      sharedWishlistContainerElement.appendChild(wishlistItem);
    } else {
      elements.itemsContainer.appendChild(wishlistItem);
    }

    window.dtDisplayTags?.();

    // Attach event listeners to swatch elements
    wishlistItem.querySelector('.add-to-cart')?.addEventListener('click', (event) => {
        const target = event.target;
        if (target.classList.contains('back-in-stock')) {
            return;
        }
        handleAddToCart(event, product);
    });
    wishlistItem.querySelector('.back-in-stock')?.addEventListener('click', (event) => {
        const backInStockOptions = {
            variantId: variant?.id,
            productId: productData?.id,
            productTitle: productData?.title,
            variantTitle: variant?.title,
        };
        const backInStockModal = new BackInStockModal(backInStockOptions);
        backInStockModal.init();
        // closeWishlist();
    })
    wishlistItem.querySelector('#swymcs-remove-productBtn')?.addEventListener('click', () => {
        for (const list of swymLists) {
            for (const item of list.listcontents) {
                if (item.empi == product.empi && item.epi == product.epi) {
                    const prod = { epi: item.epi, empi: item.empi, du: item.du };
                    swat.deleteFromList(list.lid, prod, fetchWishlist, console.error);
                }
            }
        }
    });

    return html;
}

// Helper functions (You need to define these according to your application)
function escapeHtml(text) {
    // Simple function to escape HTML special characters
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, function (m) {
        return map[m];
    });
}

const resizeImageUrl = (imageUrl, width = 500, height = 800) => {
    if (!imageUrl) return '';
    const sep = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${sep}width=${width}&height=${height}`;
}

function getImageUrl(imageSrc) {
    // Process and return the image URL
    if (imageSrc.startsWith('//')) {
        imageSrc = `https:${imageSrc}`;
    }
    return resizeImageUrl(imageSrc);
}

function formatMoney(amount) {
    // Format the price in the desired currency format
    // Assuming amount is in cents, and we want to display in NOK currency
    return new Intl.NumberFormat('nb-NO', {
        style: 'currency',
        currency: 'NOK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount / 100);
}

function hasValidSize(variants) {
    // Check if the variant(s) have valid sizes
    return variants.some((variant) => variant.option1 && variant.option1 !== '');
}

function handleQuickBuyMenu(event, productId) {
    // Handle quick buy menu opening
    event.preventDefault();
    event.stopPropagation();
    // Implement your quick buy logic here
}

function handleAddToCart(event, product, _) {
    event.preventDefault();
    event.stopPropagation();
    const cartSync = getVilloidCartSync();
    cartSync.enqueue(() => {
        return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ id: product.epi, quantity: product.qty || 1 })
        })
            .then((res) => res.json())
            .then((data) => {
                return cartSync.reconcileVariant(product.epi)
                    .catch((mergeError) => {
                        console.warn('[wishlist add-to-cart] cart reconciliation failed', mergeError);
                        return { changed: false };
                    })
                    .then((reconcileResult) => {
                        const productTitle = data.title;
                        const successMessage = (wishlistText('addedToCart') || '[title] har blitt lagt til i handlekurven!').replace('[title]', `<strong>${productTitle}</strong>`);
                        const onAddedToCart = () => {
                            closeWishlist();
                            setTimeout(() => {
                                openCartDrawer();
                            }, 100);
                        };

                        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));

                        if (reconcileResult?.changed) {
                            cartSync.refresh();
                        }

                        customSuccessHandling(successMessage, onAddedToCart);
                    });
            });
    }).catch((err) => {
        console.error('[wishlist add-to-cart]', err);
    });
}

function generateHoverMenu(productData) {
    // Generate the hover menu HTML for quick buy options
    const sortedVariants = productData.variants
        .filter((variant) => variant.available)
        .sort((a, b) => {
            // Custom sort logic for sizes (if needed)
            return a.option1.localeCompare(b.option1);
        });

    if (sortedVariants.length === 0) {
        return '';
    }

    let menuHtml = `
        <li class="ns-sidebar-dropdown ns-dropdown ns-not-facet-dropdown ns-list-unstyled ns-active">
            <a class="ns-quickbuy-title ns-sidebar-facet-title ns-product-dropdown ns-d-inline-block ns-relative ns-w-100 ns-border-box ns-text-undecorated" href="javascript:void(0)">
                ${window.theme?.strings?.addToCart || 'Legg i handlekurven'}
            </a>
            <div class="ns-sidebar-submenu">
                <ul class="ns-dropdown-wrapper ns-list-unstyled ns-p-0 ns-m-0">
    `;

    sortedVariants.forEach((variant, index) => {
        const size = variant.option1 || '';
        const inventoryLevel = variant.inventory_quantity || 0;
        const isPreOrder = false; // Adjust if you have pre-order logic

        menuHtml += `
            <li class="ns-dropdown-option ns-dropdown-option-quick-add-to-cart-added ns-d-flex ns-align-items-center" onclick="handleAddToCart(event, ${productData.id}, ${variant.id})">
                <div class="ns-option-variant-value option-variant-value">
                    ${escapeHtml(size)}
                </div>
        `;

        if (isPreOrder) {
            menuHtml += `
                <div class="ns-option-context option-${index}-left presale-true">
                    ${window.theme?.strings?.preorder || 'Forhåndsbestill'}
                </div>
            `;
        } else if (inventoryLevel > 0 && inventoryLevel < 4) {
            menuHtml += `
                <div class="ns-option-context option-${index}-left presale-false" style="color: #DB2E00;">
                    ${stockLeftText(productData?.product?.tags?.includes("SHOW_SOLD_OUT") ? inventoryLevel-1 : inventoryLevel)}
                </div>
            `;
        }

        menuHtml += `
            </li>
        `;
    });

    menuHtml += `
                </ul>
            </div>
        </li>
    `;

    return menuHtml;
}

function updateUIState(wishlist) {
    // hideLoader();
    if (wishlist.length > 0) {
        elements.container.classList.remove('swymcs-hide-container');
        elements.emptyContainer.classList.add('swymcs-hide-container');
    } else {
        elements.emptyContainer.classList.remove('swymcs-hide-container');
        elements.container.classList.add('swymcs-hide-container');
    }
}

async function fetchProductData(wishlist) {
    const productDataPromises = wishlist.map(async (listItem) => {
        const response = await fetch(`${listItem.du.split('?')[0]}.js`);
        try {
          listItem.productData = await response.json();
        } catch (e) {
          console.log(`[swym wishlist] Error fetching product data for ${listItem.du}:`, e)
        }
        return listItem;
    });
    return await Promise.all(productDataPromises);
}

function hideLoader() {
    elements.loader.classList.add('swymcs-hide-container');
}

function swymWishlistLovedAnyVariant() {
    wasRendered = false;
    swat.fetchLists({
        callbackFn: (lists) => {
            if (!lists || !lists.length) {
                console.log("Wishlist is empty or null");
                return;
            }

            const productIdsInWishlist = {};
            for (const list of lists) {
                for (const product of list.listcontents) {
                    const productId = product.empi + "";
                    if (!productIdsInWishlist[productId]) {
                        productIdsInWishlist[productId] = [];
                    }
                    productIdsInWishlist[productId].push(product.epi);
                }
            }
            const wishlistButtons = document.querySelectorAll('.swym-button.swym-add-to-wishlist-view-product.swym-heart');

            wishlistButtons.forEach(button => {
                const productId = button.getAttribute('data-product-id');
                const shouldBeLoved = productId in productIdsInWishlist;
                if (shouldBeLoved) {
                    button.classList.add('swym-added');
                    const variantId = productIdsInWishlist[productId][0]
                    button.setAttribute('data-variant-id', variantId);
                } else {
                    button.classList.remove('swym-added');
                }
            });

            document.querySelectorAll('.swym-added').forEach(
                el => {
                    const newEl = el.cloneNode(true);
                    el.parentNode.replaceChild(newEl, el);
                    newEl.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const button = e.target;
                        const productId = button.getAttribute('data-product-id');
                        for (const variantId of productIdsInWishlist[productId]) {
                            for (const list of lists) {
                                for (const item of list.listcontents) {
                                    if (item.epi == productId) {
                                        const prod = { epi: item.epi, empi: item.empi, du: item.du };
                                        swat.deleteFromList(list.lid, prod, fetchWishlist, console.error);
                                    }
                                }
                            }
                        }
                    })
                }
            );
        }
    });
}


if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
}

window.SwymCallbacks.push(onSwymLoadCallback);
window.SwymCallbacks.push(initSwymUI);
window.SwymCallbacks.push(swymWishlistLovedAnyVariant);

// Initialize wishlist count on page load
// Injected after DOMContentLoaded on the PDP, so don't wait for an event that already fired.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        setTimeout(updateWishlistCount, 1000); // Small delay to ensure SWYM is loaded
    });
} else {
    setTimeout(updateWishlistCount, 1000);
}
